import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Autocomplete,
    FormControl,
    FormControlLabel,
    Checkbox,
    Button,
    IconButton,
    Switch,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ArrowLeft, Plus, Minus, ChevronLeft, User } from "react-feather";
import { Link } from "react-router-dom";
import Toastmessage from '../../components/Toast';
import { authApi } from '../../api/api';
import { useSelector } from 'react-redux';
import { logout } from '../../features/auth/AuthSlice';
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom';


const EditSubscriptionForm = () => {
    const [modules, setModules] = useState([]); // State to store fetched modules
    const [errors, setErrors] = useState({})
    const [active, setActive] = useState(true);
    const [isLoading, setIsloading] = useState(false);

    const token = useSelector((state) => state.auth.token); // Get the token from Redux
    const userID = useSelector((state) => state.auth.id); // Get the userID from Redux

    const dispatch = useDispatch()
    const navigate = useNavigate();

    const location = useLocation();
    const { subscriptionsId } = location.state || {}; // Retrieve the ID


    const durationOptions = Array.from({ length: 12 }, (_, i) => `${i + 1} Month`);

    const [formData, setFormData] = useState({
        login_user_id: userID, // Dynamically set userID to formData
        subscription_name: "",
        price: [{ month: null, actual_price: "", discount_price: "" }], // Initial row
        access_modules: [],
        status:''
    });

    useEffect(() => {
        if (subscriptionsId) {
            editsetsubscription(subscriptionsId); // Call editLocation directly with the id
            fetchModule();
        }
    }, [subscriptionsId]); // Only runs when locationId changes, so editLocation is called once.

    // Define fetchModule separately to reuse it
    const fetchModule = async () => {
        const input_token = 'MODULES'; // Replace with your actual token
        const apiUrl = 'list_of_modules'; // Replace with the actual API endpoint

        const dataInput = {
        };

        try {
            const data = await authApi(input_token, dataInput, apiUrl, token);


            if (data && data.success) {
                // Set filtered rows to the same data initially
                setModules(data.access_modules);
            } else if (data && data.message === 'Token has expired') {
                dispatch(logout());
                localStorage.removeItem('token');
                await Toastmessage('Session expired. Redirecting to login...', false);
                navigate('/login');
            } else if (data === undefined) {
                await Toastmessage('No response from server', false);
            } else {
                await Toastmessage(data?.message || 'An error occurred', false);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
            if (error.response && error.response.status === 401) {
                dispatch(logout());
                localStorage.removeItem('token');
                await Toastmessage('Session expired. Redirecting to login...', false);
                navigate('/login');
            } else {
                await Toastmessage('Something went wrong', false);
            }
        }
    };

    const editsetsubscription = async (subscriptionsId) => {
        if (!subscriptionsId) return; // Ensure subscriptionsId is available

        const input_token = subscriptionsId + 'VIEW_SUBS'; // Replace with your actual token
        const apiUrl = 'view_subscription'; // Replace with the actual API endpoint

        const dataInput = {
            id: subscriptionsId,
            login_user_id: userID,
        };

        try {
            const data = await authApi(input_token, dataInput, apiUrl, token);

            // Check if data contains the subscription details
            if (data && data.data) {
                const {
                    subscription_id,
                    subscription_name,
                    price, // Array of price objects { month, actual_price, discount_price }
                    access_modules, // Array of selected module IDs
                    status
                } = data.data;

                // Update the formData state with the fetched data
                setActive(!!status)
                setFormData({
                    id: subscription_id,
                    login_user_id: userID, // Keep the current userID
                    subscription_name: subscription_name || "", // Populate subscription name
                    price: price || [{ month: null, actual_price: "", discount_price: "" }], // Populate price rows
                    access_modules: access_modules || [], // Populate access modules
                    status:!!status
                });
            } else {
                // Handle cases where the data is not as expected
                await Toastmessage(data?.message || "Failed to fetch subscription details", false);
            }
        } catch (error) {
            console.error("Error fetching subscription data:", error);
            await Toastmessage("Something went wrong while fetching subscription details", false);
        }
    };


    const handleActiveChange = (event) => {
        const isChecked = event.target.checked;
        setActive(isChecked); // Update state directly
        setFormData({
            ...formData,
            status: isChecked ? 1 : 0 // Pass 1 for true and 0 for false
        });
    };


    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubscriptionChange = (index, field, value) => {
        // Clone the current form data and update the specific field for the corresponding subscription index
        const updatedSubscriptions = [...formData.price];
        updatedSubscriptions[index] = {
            ...updatedSubscriptions[index], // Keep previous values for other fields
            [field]: value, // Update the specific field
        };

        // Update the state with the modified subscription list
        setFormData((prev) => ({ ...prev, price: updatedSubscriptions }));
    };

    const handleAddRow = () => {
        setFormData((prev) => ({
            ...prev,
            price: [...prev.price, { month: null, actual_price: "", discount_price: "" }],
        }));
    };

    const handleRemoveRow = (index) => {
        const updatedSubscriptions = formData.price.filter((_, i) => i !== index);
        setFormData((prev) => ({ ...prev, price: updatedSubscriptions }));
    };


    const handleModuleChange = (moduleId) => {
        setFormData((prev) => ({
            ...prev,
            access_modules: prev.access_modules.includes(moduleId)
                ? prev.access_modules.filter((id) => id !== moduleId) // Remove if already selected
                : [...prev.access_modules, moduleId], // Add if not selected
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate Subscription Name
        if (!formData.subscription_name || formData.subscription_name.trim() === "") {
            newErrors.subscription_name = "Subscription name is required";
        }

        // Validate Subscription Price Rows
        formData.price.forEach((subscription, index) => {
            if (!subscription.month) {
                newErrors[`month-${index}`] = "Subscription month is required";
            }
            if (!subscription.actual_price || isNaN(subscription.actual_price)) {
                newErrors[`actual_price-${index}`] = "Valid regular cost is required";
            }
            if (!subscription.discount_price || isNaN(subscription.discount_price)) {
                newErrors[`discount_price-${index}`] = "Valid sales cost is required";
            }
        });

        // Validate Modules
        if (formData.access_modules.length === 0) {
            newErrors.access_modules = "At least one module must be selected";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSubmit = (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return; // Exit if validation fails
        }

        const addSubApi = async () => {
            setIsloading(true);
            const input_token = subscriptionsId + 'UPDATE_SUBS';
            const apiUrl = 'update_subscription';

            try {
                const data = await authApi(input_token, formData, apiUrl, token, true);
                
                if (data && data.success) {
                    await Toastmessage(data.message, true);
                    navigate('/subscriptions');
               
                } else if (data && data.message === 'Token has expired') {
                    dispatch(logout());
                    setIsloading(false);
                    localStorage.removeItem('token');
                    await Toastmessage('Session expired. Redirecting to login...', false);
                    navigate('/login');
                } else if (data === undefined) {
                    setIsloading(false);
                    await Toastmessage('No response from server', false);
                } else {
                    setIsloading(false);
                    await Toastmessage(data?.message || 'An error occurred', false);
                }
            } catch (error) {
                setIsloading(false);
                console.error("Error fetching data:", error);
                if (error.response && error.response.status === 401) {
                    dispatch(logout());
                    localStorage.removeItem('token');
                    await Toastmessage('Session expired. Redirecting to login...', false);
                    navigate('/login');
                } else {
                    await Toastmessage('Something went wrong', false);
                }
            }
        };

        addSubApi();

    };

    const RequiredLabel = ({ children }) => (
        <>
            {children} <span style={{ color: 'red' }}>*</span>
        </>
    );

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom='20px'
                backgroundColor='var(--theme-bg-light)'
                padding={2}
                borderRadius={1}
            >
                {/* Left Section */}
                <Box display="flex" alignItems="center">
                    <User style={{ marginRight: 8, fontSize: '12px', color: 'var(--icon-color)'}} />
                    <Typography fontSize={12} color="var(--text-color)">
                        Subscriptions  List
                    </Typography>
                </Box>

                {/* Right Section */}
                <Button
                    variant="outlined"
                    component={Link}
                    to="/subscriptions"
                    sx={{
                        backgroundColor: 'var(--back-button)',
                        color: "var(--back-color)",
                        fontWeight: 'bold',
                        border: 'none',
                        padding: '10px 20px'
                    }}
                    startIcon={<ChevronLeft sx={{ fontSize: 17 }} />}
                    style={{ textTransform: 'none' }}
                >
                    Back to Subscriptions list
                </Button>
            </Box>
            <Box className="card-box" sx={{ p: { xs: 2, sm: 2, md: 3 }, m: { xs: 1, sm: 2, md: 3 }, border: '1px solid var(--border-color)', borderRadius: '8px' }}>

                {/* Header Section */}
                <Box className="sub-header">
                    <Typography sx={{ color: "#3570FF", fontSize: "14px", fontWeight: "bold" }}>
                        <Link
                            to="/subscriptions"
                            style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
                        >
                            <ArrowLeft size={20} style={{ marginRight: "8px" }} />
                            Edit Subscription
                        </Link>
                    </Typography>
                    <FormControlLabel
                        control={<Switch checked={active} onChange={handleActiveChange} />}
                        label={
                            <Typography sx={{ color: active ? 'green' : 'red', fontWeight: 'bold' }}>
                                Active : {active ? 'Yes' : 'No'}
                            </Typography>
                        }
                    />
                </Box>

                <Grid container spacing={18}>
                    {/* Left Section */}
                    <Grid size={8}>
                        {/* Subscription Name */}
                        <Box sx={{ marginBottom: 2 }}>
                            <Typography
                                variant="body2"
                                sx={{ marginBottom: 0.5, color: "#8A8A8A" }}
                            >
                                <RequiredLabel>Subscription Full Name</RequiredLabel>
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter the Subscription name"
                                variant="outlined"
                                value={formData.subscription_name} // Bound to formData
                                error={!!errors.subscription_name}
                                helperText={errors.subscription_name}
                                onChange={(e) => handleInputChange("subscription_name", e.target.value)}
                            />
                        </Box>

                        {/* Dynamic Subscription Rows */}
                        {formData.price.map((subscription, index) => (
                            <Grid
                                container
                                spacing={3}
                                key={index}
                                sx={{
                                    marginBottom: 2,
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {/* Subscription Duration */}
                                <Grid size={3}>
                                    <Typography
                                        variant="body2"
                                        sx={{ marginBottom: 0.5, color: "#8A8A8A" }}
                                    >
                                        <RequiredLabel>Subscription Month</RequiredLabel>
                                    </Typography>
                                    <Autocomplete
                                        options={durationOptions}
                                        getOptionLabel={(option) => `${option}`} // Ensure the option is a string
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Select month"
                                                error={!!errors[`month-${index}`]} // Show error if exists
                                                helperText={errors[`month-${index}`]} // Display error message
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                                            />
                                        )}
                                        value={subscription.month || ""}
                                        onChange={(e, newValue) => {
                                            const numericValue = Number(newValue?.split(" ")[0]); // Extract numeric value
                                            handleSubscriptionChange(index, "month", numericValue);
                                        }}
                                    />

                                </Grid>

                                {/* Subscription Regular Cost */}
                                <Grid size={4}>
                                    <Typography
                                        variant="body2"
                                        sx={{ marginBottom: 0.5, color: "#8A8A8A" }}
                                    >
                                        <RequiredLabel>Subscription Regular Cost (USD)</RequiredLabel>
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter the regular cost"
                                        variant="outlined"
                                        error={!!errors[`actual_price-${index}`]} // Show error if exists
                                        helperText={errors[`actual_price-${index}`]} // Display error message
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                                        value={subscription.actual_price || ""}
                                        onChange={(e) => {
                                            let value = e.target.value;

                                            // Allow only numeric values and disallow leading zero unless it's the only value
                                            if (/^\d*$/.test(value)) {
                                            if (value === "0" || !value.startsWith("0")) {
                                                // Allow up to 5 digits
                                                if (value.length <= 5) {
                                                handleSubscriptionChange(index, "actual_price", value);
                                                }
                                            }
                                            }
                                        }}
                                    />

                                </Grid>

                                {/* Subscription Sales Cost */}
                                <Grid size={4}>
                                    <Typography
                                        variant="body2"
                                        sx={{ marginBottom: 0.5, color: "#8A8A8A" }}
                                    >
                                        <RequiredLabel>Subscription Sales Cost (USD)</RequiredLabel>
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter the sales cost"
                                        variant="outlined"
                                        error={!!errors[`discount_price-${index}`]} // Show error if exists
                                        helperText={errors[`discount_price-${index}`]} // Display error message
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                                        value={subscription.discount_price || ""}
                                        onChange={(e) => {
                                            let value = e.target.value;

                                            // Allow only numeric values and disallow leading zero unless it's the only value
                                            if (/^\d*$/.test(value)) {
                                            if (value === "0" || !value.startsWith("0")) {
                                                // Allow up to 5 digits
                                                if (value.length <= 5) {
                                                handleSubscriptionChange(index, "discount_price", value);
                                                }
                                            }
                                            }
                                        }}
                                        
                                    />

                                </Grid>

                                {/* Add/Remove Buttons */}
                                <Grid size={1} sx={{ textAlign: "center", marginTop: "20px" }}>
                                    {index === formData.price.length - 1 ? (
                                        <IconButton onClick={handleAddRow} color="primary">
                                            <Plus size={20} />
                                        </IconButton>
                                    ) : (
                                        <IconButton onClick={() => handleRemoveRow(index)} color="error">
                                            <Minus size={20} />
                                        </IconButton>
                                    )}
                                </Grid>
                            </Grid>
                        ))}

                    </Grid>

                    {/* Right Section */}
                    <Grid size={4}>
                        <Typography
                            variant="subtitle1"
                            sx={{ marginBottom: 1, color: "#333333" }}
                        >
                            <RequiredLabel>Enable/Disable Modules</RequiredLabel>
                        </Typography>
                        <FormControl>
                            {modules.map((module) => (
                                <FormControlLabel
                                    key={module.id}
                                    control={
                                        <Checkbox
                                            checked={formData.access_modules.includes(module.id)}
                                            onChange={() => handleModuleChange(module.id)}
                                            sx={{
                                                transform: "scale(0.7)",
                                                borderRadius: "8px",
                                                padding: "2px",
                                                "&.Mui-checked": {
                                                    color: "#3570FF",
                                                },
                                            }}
                                        />
                                    }
                                    label={module.module_name}
                                    sx={{
                                        marginBottom: 3,
                                        color: "#8A8A8A",
                                    }}
                                />
                            ))}
                            {errors.access_modules && (
                                <Typography variant="caption" color="error">
                                    {errors.access_modules}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Button Section */}
                <Box textAlign="center" sx={{ marginTop: 4 }}>
                    <Button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        variant="contained"
                        className='btn-theme-button'
                    >
                        Update Subscriptions
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default EditSubscriptionForm;
